import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { getConfig } from "../config";
import { apiFetch } from "../api/apiFetch";
import { ApiError, type AuthorizationResult } from "../types";
import { Spinner } from "../components/Spinner";
import { AccessDenied, ServiceError, InternalNoGroup, InternalInvalidGroup, GroupPicker } from "./GroupGuardPages";

interface GroupGuardProps {
  children: ReactNode;
}

/**
 * After authentication, checks group/opportunity authorization via the API.
 * Accepts oppId (primary) or groupId (fallback) from the URL.
 */
export function GroupGuard({ children }: GroupGuardProps) {
  const [searchParams] = useSearchParams();
  const oppId = searchParams.get("oppId");
  const groupId = searchParams.get("groupId");
  const hasIdentifier = !!oppId || !!groupId;

  const config = getConfig();

  const [authResult, setAuthResult] = useState<AuthorizationResult | null>(
    config.AUTH_ENABLED ? null : { isAuthorized: true, role: "Internal", groups: [] }
  );
  const [authError, setAuthError] = useState<"denied" | "service" | null>(null);
  const fetchedRef = useRef(!config.AUTH_ENABLED);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    // Build query: oppId takes precedence over groupId
    const params = new URLSearchParams();
    if (oppId) params.set("oppId", oppId);
    else if (groupId) params.set("groupId", groupId);
    const query = params.toString() ? `?${params}` : "";

    apiFetch<AuthorizationResult>(`/employer-intake/authorize${query}`, {
      allowedStatuses: [403],
    })
      .then((result) => {
        if (!result || typeof result.isAuthorized !== "boolean") {
          setAuthError("service");
          return;
        }
        setAuthResult(result);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 403) {
          // 403 carries an AuthorizationResult body (e.g. user has groups but not this one).
          const body = err.body as AuthorizationResult | undefined;
          if (body && typeof body.isAuthorized === "boolean") {
            setAuthResult(body);
            return;
          }
          setAuthError("denied");
          return;
        }
        setAuthError("service");
      });
  }, [oppId, groupId]);

  const state = useMemo<
    | { status: "loading" }
    | { status: "authorized" }
    | { status: "denied" }
    | { status: "service-error" }
    | { status: "internal-no-group" }
    | { status: "internal-invalid-group" }
    | { status: "picker"; groups: AuthorizationResult["groups"]; message?: string }
    | { status: "redirect"; to: string }
  >(() => {
    if (authError === "service") return { status: "service-error" };
    if (authError === "denied") return { status: "denied" };
    if (!authResult) return { status: "loading" };

    if (!authResult.isAuthorized) {
      if (authResult.role === "Internal") return { status: "internal-invalid-group" };
      if (authResult.groups.length > 0) {
        return {
          status: "picker",
          groups: authResult.groups,
          message: hasIdentifier
            ? "The requested group could not be found or you don't have access. Please select one below."
            : undefined,
        };
      }
      return { status: "denied" };
    }

    if (authResult.role === "Internal") {
      return hasIdentifier ? { status: "authorized" } : { status: "internal-no-group" };
    }

    if (authResult.groups.length === 0) return { status: "denied" };

    if (!hasIdentifier) {
      if (authResult.groups.length === 1) {
        const group = authResult.groups[0];
        return {
          status: "redirect",
          to: group.oppId
            ? `/intake?oppId=${encodeURIComponent(group.oppId)}`
            : `/intake?groupId=${encodeURIComponent(group.accountId)}`,
        };
      }
      return { status: "picker", groups: authResult.groups };
    }

    // Authorized: the API named this opportunity, or it belongs to one of the groups the API authorized. With an oppId,
    // only the oppId decides, as on the API; ids compare without case, as the API's do.
    const isAuthorized = oppId
      ? isSameId(authResult.oppId, oppId) || authResult.groups.some((g) => isSameId(g.oppId, oppId))
      : authResult.groups.some((g) => isSameId(g.accountId, groupId));
    if (isAuthorized) return { status: "authorized" };
    return {
      status: "picker",
      groups: authResult.groups,
      message: "You don't have access to the requested group. Please select one below.",
    };
  }, [authResult, authError, hasIdentifier, oppId, groupId]);

  if (state.status === "loading") return <Spinner overlay />;
  if (state.status === "service-error") return <ServiceError />;
  if (state.status === "denied") return <AccessDenied />;
  if (state.status === "internal-no-group") return <InternalNoGroup />;
  if (state.status === "internal-invalid-group") return <InternalInvalidGroup />;
  if (state.status === "picker") return <GroupPicker groups={state.groups} message={state.message} />;
  if (state.status === "redirect") return <Navigate to={state.to} replace />;

  return children;
}

function isSameId(id: string | null | undefined, requested: string | null): boolean {
  return !!id && !!requested && id.toLowerCase() === requested.toLowerCase();
}
